/* Redirects unauthenticated users to sign-in, and new users to onboarding wizard.
   Shows a single kiwi loading screen through all loading phases (auth sync,
   query delay, onboarding check) to avoid animation restarts. */

import { useState, useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { FeedItemsProvider } from "@/contexts/FeedItemsProvider";
import { ConvexQueryErrorBoundary } from "@/components/auth/ConvexQueryErrorBoundary";
import { RoutePath } from "@/routePaths";
import { useOnboardingGate } from "@/hooks/useOnboardingGate";
import { AnimatedKiwiIcon } from "@/components/chat/KiwiChatButton";

/** Delay before running feed queries so Convex auth is fully applied. */
const FEED_QUERY_DELAY_MS = 400;
/** How long before showing a "refresh" hint on the loading screen. */
const SYNC_TIMEOUT_MS = 8000;

interface ProtectedRouteProps {
  userReady: boolean;
}

export function ProtectedRoute({ userReady }: ProtectedRouteProps) {
  const { isSignedIn, isLoaded, user } = useUser();
  const location = useLocation();
  const [queryReady, setQueryReady] = useState(false);
  const [showRefreshHint, setShowRefreshHint] = useState(false);
  const { shouldRedirectToOnboarding, isLoading: isOnboardingLoading } = useOnboardingGate(userReady);

  // Start query delay timer only after both Clerk and Convex auth are ready
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !userReady) return;
    const t = setTimeout(() => setQueryReady(true), FEED_QUERY_DELAY_MS);
    return () => clearTimeout(t);
  }, [isLoaded, isSignedIn, userReady]);

  // Show refresh hint if Convex auth sync takes too long
  useEffect(() => {
    if (userReady || !isLoaded || !isSignedIn) {
      setShowRefreshHint(false);
      return;
    }
    const t = setTimeout(() => setShowRefreshHint(true), SYNC_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [isLoaded, isSignedIn, userReady]);

  // Redirects (only when Clerk auth is loaded)
  if (isLoaded && !isSignedIn) {
    return <Navigate to={RoutePath.SignIn} replace />;
  }

  if (isLoaded && isSignedIn && user?.emailAddresses[0]?.verification?.status !== "verified") {
    return <Navigate to={RoutePath.SignUp} replace />;
  }

  // Single loading screen for all phases: Clerk loading, Convex auth sync,
  // query delay, and onboarding status check
  if (!isLoaded || !userReady || !queryReady || isOnboardingLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background gap-4 px-4">
        <AnimatedKiwiIcon eyeAnimation="slowScan" eyeSize="tallPupil" speed={1.5} loop className="size-24" />
        <p className="text-muted-foreground text-sm">Loading...</p>
        {showRefreshHint && (
          <div className="text-center text-muted-foreground text-sm">
            <p className="mb-2">Taking a while? Your session may not be synced yet.</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="text-primary underline hover:no-underline"
            >
              Refresh the page
            </button>
          </div>
        )}
      </div>
    );
  }

  if (shouldRedirectToOnboarding && location.pathname !== RoutePath.Onboarding) {
    return <Navigate to={RoutePath.Onboarding} replace />;
  }

  return (
    <ConvexQueryErrorBoundary>
      <FeedItemsProvider>
        <Outlet />
      </FeedItemsProvider>
    </ConvexQueryErrorBoundary>
  );
}
